import { SIGNAL_META } from '@routr/shared'
import { portIdFromHandle, type PortDTO } from '@routr/shared'

/**
 * Whether one port may be patched to another, and why not.
 *
 * These rules are the heart of the canvas — they decide what a user is
 * allowed to draw — and they lived inside a 1,400-line client component, which
 * meant the only way to check them was to run a browser and drag something.
 * They depend on nothing but their arguments, so they live here and are
 * tested directly.
 *
 * The reason comes back with the refusal so the same call can both block a drag
 * and explain it afterwards. A null reason means "not a connection at all"
 * (a missing end), which needs no message.
 */
export type ConnectionVerdict =
  | {
      ok: true
      /**
       * The cable already on this output, which the new connection moves
       * rather than joining: an output feeds one cable, so drawing a second
       * from it means "plug it in over there instead".
       */
      moves?: string
    }
  | { ok: false; reason: string | null }

/** Just enough of a React Flow edge to tell which ports are taken. */
export type ConnectionEdge = {
  id?: string
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
}

export type ConnectionEnds = {
  source: string | null | undefined
  sourceHandle: string | null | undefined
  target: string | null | undefined
  targetHandle: string | null | undefined
}

export function evaluateConnection(
  portById: Map<string, PortDTO>,
  allEdges: ConnectionEdge[],
  { source, sourceHandle, target, targetHandle }: ConnectionEnds,
  /**
   * `reconnecting`: the id of a cable whose end is being dragged to a new
   * port. It does not count as occupying anything, and it cannot land on an
   * output that already has a cable, since that would leave two on one plug.
   */
  options: { reconnecting?: string } = {},
): ConnectionVerdict {
  const edges = options.reconnecting ? allEdges.filter((edge) => edge.id !== options.reconnecting) : allEdges
  if (!source || !target || !sourceHandle || !targetHandle) {
    return { ok: false, reason: null }
  }
  if (source === target) {
    return { ok: false, reason: 'A device cannot be patched to itself.' }
  }

  const sourcePort = portById.get(portIdFromHandle(sourceHandle))
  const targetPort = portById.get(portIdFromHandle(targetHandle))
  if (!sourcePort || !targetPort) return { ok: false, reason: null }

  // A signal only goes where that signal can go, and the message names both
  // ends so the refusal is actionable rather than a shrug.
  if (sourcePort.signal !== targetPort.signal) {
    const from = SIGNAL_META[sourcePort.signal].label.toLowerCase()
    const into = SIGNAL_META[targetPort.signal].label.toLowerCase()
    const article = /^[aeiou]/.test(into) ? 'an' : 'a'
    return {
      ok: false,
      reason:
        `Cannot patch ${from} (${sourcePort.name}, ${sourcePort.connector}) ` +
        `into ${article} ${into} input (${targetPort.name}, ${targetPort.connector}).`,
    }
  }

  // A physical input takes one cable. This is a fact about connectors, not a
  // policy, which is why it is enforced here and by a unique index.
  if (edges.some((edge) => edge.target === target && edge.targetHandle === targetHandle)) {
    return { ok: false, reason: `${targetPort.name} already has a cable in it.` }
  }

  const occupied = (nodeId: string, portId: string) =>
    edges.some(
      (edge) =>
        (edge.source === nodeId &&
          edge.sourceHandle &&
          portIdFromHandle(edge.sourceHandle) === portId) ||
        (edge.target === nodeId &&
          edge.targetHandle &&
          portIdFromHandle(edge.targetHandle) === portId),
    )

  // A bi-directional port is one connector drawn as two handles: it carries
  // one cable, in whichever direction it was patched.
  if (sourcePort.direction === 'BIDIRECTIONAL' && occupied(source, sourcePort.id)) {
    return {
      ok: false,
      reason: `${sourcePort.name} is bi-directional and already in use — it carries one cable, in one direction.`,
    }
  }
  if (targetPort.direction === 'BIDIRECTIONAL' && occupied(target, targetPort.id)) {
    return {
      ok: false,
      reason: `${targetPort.name} is bi-directional and already in use — it carries one cable, in one direction.`,
    }
  }

  // An output also takes one cable. Drawing a second from it moves the one
  // that is there; dragging some other cable's end onto it is refused, and
  // the way to feed two inputs is a splitter.
  const onOutput = edges.find(
    (edge) => edge.source === source && edge.sourceHandle && portIdFromHandle(edge.sourceHandle) === sourcePort.id,
  )
  if (sourcePort.direction === 'OUTPUT' && onOutput) {
    if (options.reconnecting) {
      return {
        ok: false,
        reason: `${sourcePort.name} already has a cable. To feed two inputs from it, right-click that cable and choose Split.`,
      }
    }
    return { ok: true, ...(onOutput.id ? { moves: onOutput.id } : {}) }
  }

  return { ok: true }
}

/**
 * How many outputs feed more than one cable. The canvas now moves a cable
 * rather than adding a second to an output, but diagrams drawn before that
 * can still have them, and each one needs a splitter.
 */
export function sharedOutputs(portById: Map<string, PortDTO>, edges: ConnectionEdge[]): number {
  const counts = new Map<string, number>()
  for (const edge of edges) {
    if (!edge.sourceHandle) continue
    const portId = portIdFromHandle(edge.sourceHandle)
    if (portById.get(portId)?.direction !== 'OUTPUT') continue
    const key = `${edge.source} ${portId}`
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  return [...counts.values()].filter((n) => n > 1).length
}
